import { useParams, Link, useNavigate } from 'react-router-dom'
import { useChurch } from '../hooks/useChurches'
import { useClaimChurch } from '../hooks/useChurches'
import { useAuthStore } from '../store/auth'
import { PublicLayout } from '../components/layout/PublicLayout'
import { Icons } from '../components/ui/Icons'
import { track } from '../lib/analytics'

export default function ClaimChurchPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: churchData, isLoading } = useChurch(id)
  const { user } = useAuthStore()
  const claim = useClaimChurch(id ?? '')

  const church = churchData?.data

  const handleClaim = () => {
    claim.mutate(undefined, {
      onSuccess: (res) => {
        const slug = res.data.slug
        track('church_claim', { church_id: id, slug })
        navigate(`/church-onboarding/${slug}`)
      },
    })
  }

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 80px)' }}>
          <p className="text-[13px] text-muted">Loading…</p>
        </div>
      </PublicLayout>
    )
  }

  if (!church) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center flex-col gap-3" style={{ height: 'calc(100vh - 80px)' }}>
          <p className="text-sm text-ink-2">Church not found.</p>
          <Link to="/churches" className="text-primary text-[13px] no-underline font-medium">← Browse churches</Link>
        </div>
      </PublicLayout>
    )
  }

  if (church.claimed) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center flex-col gap-4 px-6 text-center" style={{ height: 'calc(100vh - 80px)' }}>
          <div className="text-[40px]">🏛️</div>
          <p className="serif text-2xl m-0">This church has already been claimed.</p>
          <Link to={`/churches/${church.slug}`} className="text-primary text-sm no-underline font-medium">View profile →</Link>
        </div>
      </PublicLayout>
    )
  }

  const trafficNote = church.viewCount > 0 || church.planToAttendCount > 0

  return (
    <PublicLayout>
      <div className="max-w-[560px] mx-auto px-6 py-14">
        <div className="mb-2">
          <span className="chip bg-[#fff8e1] text-warn border-warn text-xs">Unverified listing</span>
        </div>

        <h1 className="serif m-0 mt-3 mb-1.5 text-[40px] leading-[1.05]">
          Claim {church.name}
        </h1>

        <p className="text-[15px] text-ink-2 leading-[1.6] mb-8">
          We found this church online and listed its public events. Claim your page to take control — manage your own schedule, enable RSVPs, and remove the "unverified" label.
        </p>

        {trafficNote && (
          <div className="px-5 py-[18px] rounded-[14px] mb-7 bg-primary-soft border border-primary">
            <div className="eyebrow mb-2.5 text-primary">Your events are already getting traffic</div>
            <div className="flex gap-8">
              <div className="text-center">
                <div className="serif text-4xl text-primary leading-none">{church.viewCount}</div>
                <div className="text-xs text-ink-2 mt-1">page views</div>
              </div>
              <div className="text-center">
                <div className="serif text-4xl text-primary leading-none">{church.planToAttendCount}</div>
                <div className="text-xs text-ink-2 mt-1">plan-to-attend taps</div>
              </div>
            </div>
          </div>
        )}

        <div className="mb-8">
          <div className="text-[13px] font-semibold text-ink mb-3">What you get when you claim</div>
          {[
            ['Remove the "unverified" warning', 'Your events show as official — no disclaimer banner'],
            ['Manage your own event schedule', 'Add, edit, and cancel events directly in Fellowship'],
            ['RSVP management', 'See who\'s coming, manage dish signups, broadcast to attendees'],
            ['Traffic dashboard', 'See how many people are finding your events on Fellowship'],
          ].map(([title, desc]) => (
            <div key={title} className="flex gap-3 mb-3.5">
              <span className="text-primary shrink-0 mt-0.5"><Icons.check size={16} /></span>
              <div>
                <div className="text-sm font-medium text-ink">{title}</div>
                <div className="text-[13px] text-ink-2 mt-0.5">{desc}</div>
              </div>
            </div>
          ))}
        </div>

        {!user ? (
          <div className="px-[22px] py-5 rounded-[14px] bg-s2 border border-line-soft">
            <div className="text-sm font-semibold mb-1.5">Create a free account to claim this page</div>
            <p className="text-[13px] text-ink-2 m-0 mb-4 leading-[1.5]">
              Claiming is free. No credit card needed until you want to enable RSVPs.
            </p>
            <div className="flex gap-2.5">
              <Link
                to={`/register?next=/churches/${church.id}/claim`}
                className="flex-1 flex items-center justify-center h-11 rounded-[10px] bg-primary text-on-primary text-sm font-semibold no-underline"
              >
                Create account
              </Link>
              <Link
                to={`/login?next=/churches/${church.id}/claim`}
                className="flex-1 flex items-center justify-center h-11 rounded-[10px] border border-line text-ink text-sm font-medium no-underline"
              >
                Log in
              </Link>
            </div>
          </div>
        ) : claim.isSuccess ? (
          <div>
            <div className="px-[22px] py-5 rounded-[14px] bg-primary-soft border border-primary text-center mb-4">
              <div className="text-[28px] mb-2">✓</div>
              <div className="serif text-[22px] mb-2">Page claimed!</div>
              <p className="text-[13px] text-ink-2 m-0 mb-4">
                You're now the owner of {church.name}'s Fellowship page.
              </p>
              <Link
                to={`/churches/${church.slug}/dashboard`}
                className="text-primary font-semibold no-underline text-sm"
              >
                Go to your dashboard →
              </Link>
            </div>

            <div className="px-[22px] py-5 rounded-[14px] bg-s2 border border-line">
              <div className="text-[13px] font-semibold text-ink mb-1.5">
                Ready to enable RSVPs?
              </div>
              <p className="text-[13px] text-ink-2 m-0 mb-4 leading-[1.5]">
                A subscription unlocks RSVP management, dish coordination, host tools, and the broadcast email. Your first 14 days are free.
              </p>
              <Link
                to={`/churches/${church.slug}/billing`}
                className="flex items-center justify-center h-[42px] rounded-[10px] bg-primary text-on-primary text-sm font-semibold no-underline"
              >
                Start free trial
              </Link>
            </div>
          </div>
        ) : (
          <div>
            {claim.isError && (
              <div role="alert" className="px-3.5 py-2.5 rounded-lg bg-[#fef2f2] border border-[#fca5a5] text-[#b91c1c] text-[13px] mb-3.5">
                {(claim.error as Error)?.message ?? 'Something went wrong'}
              </div>
            )}
            <button
              onClick={handleClaim}
              disabled={claim.isPending}
              className="w-full h-12 rounded-xl bg-primary text-on-primary border-0 text-[15px] font-semibold cursor-pointer disabled:opacity-70"
            >
              {claim.isPending ? 'Claiming…' : `Claim ${church.name}`}
            </button>
            <p className="text-xs text-muted text-center mt-2.5">
              Free to claim. By claiming you confirm you represent this church.
            </p>
          </div>
        )}
      </div>
    </PublicLayout>
  )
}
